let activeCleanup: VoidFunction | null = null;

/**
 * Creates and adds a "Loading Sign Order" animation to the DOM, including elements such as
 * a document structure, loading text, and a progress bar. Also returns a cleanup
 * handler to remove these elements and the associated loading state from the DOM.
 *
 * Idempotent: if the animation is already running, returns the existing cleanup function
 * without creating duplicate elements.
 *
 * @return {Function} A function that, when executed, removes the created animation elements
 * and cleaning up the loading state.
 */
export function createLoadingAndClearAnimationHandler(): VoidFunction {
  if (activeCleanup) return activeCleanup;

  // Add loading class to the body and create document animation elements
  const hostContainer = document.createElement('div');
  hostContainer.classList.add('container-host');
  document.body.appendChild(hostContainer);
  // Create a document structure
  const docContainer = document.createElement('div');
  docContainer.classList.add('contract-container');

  // Document text lines
  const textLines = document.createElement('div');
  textLines.classList.add('contract-text-lines');

  // Signature line
  const signatureLine = document.createElement('div');
  signatureLine.classList.add('signature-line');

  // loadingText
  const loadingText = document.createElement('div');
  loadingText.classList.add('contract-loading-text');
  loadingText.textContent = 'Loading';

  // Progress bar
  const progressBar = document.createElement('div');
  progressBar.classList.add('contract-progress-bar');

  const progressFill = document.createElement('div');
  progressFill.classList.add('contract-progress-fill');

  progressBar.appendChild(progressFill);

  // Assemble document
  docContainer.appendChild(textLines);
  docContainer.appendChild(signatureLine);
  hostContainer.appendChild(docContainer);
  // Add all elements to the body
  document.body.appendChild(docContainer);
  document.body.appendChild(loadingText);
  document.body.appendChild(progressBar);

  activeCleanup = () => {
    activeCleanup = null;
    // Remove loading class and all animation elements
    document.body.classList.remove('container-host');
    if (document.body.contains(docContainer)) document.body.removeChild(docContainer);
    if (document.body.contains(loadingText)) document.body.removeChild(loadingText);
    if (document.body.contains(progressBar)) document.body.removeChild(progressBar);
    if (document.body.contains(hostContainer)) document.body.removeChild(hostContainer);
  };

  return activeCleanup;
}

/**
 * Removes any lingering loading animation elements from the DOM.
 * Useful when navigating to routes that have no resolver (e.g. error/not-found pages)
 * where no cleanup function is ever called.
 */
export function clearAllLoadingAnimations(): void {
  activeCleanup?.();
}
